import { Autocomplete, Button, Card, Divider, Grid, OutlinedInput, Stack, Typography } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import { cacheService, fileService, ingestService } from "../../../Core/Api/services";
import { ProviderAndAsperaConfiguration } from "../../../Core/Api/Types/provider-and-aspera-configuration";
import { PlatformDocumentType } from "../../../Core/Api/Types/platform-document-type";
import { PlatformDocument } from "../../../Core/Api/Types/platform-document";
import DataTable, { CellSize } from "../../Table/table";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import moment from "moment";
import { OpenLinkInNewTab, UserHasAcl } from "../../../Utils/helpers";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { useTranslation } from "react-i18next";

export type ModalAsperaCredentialsElementProps = {};

export type ModalAsperaCredentialsProps = CommonModalProps<ModalAsperaCredentialsElementProps>;

const ModalAsperaCredentials = React.forwardRef<any, ModalAsperaCredentialsProps>((props, ref) => {
    const { closeModal, ...modalProps } = props;
    const { t } = useTranslation();
    const [settings, setSettings] = React.useState<ProviderAndAsperaConfiguration[]>([]);
    const [settingsCurrent, setSettingCurrent] = React.useState<ProviderAndAsperaConfiguration>();
    const [docs, setDocs] = React.useState<PlatformDocument[]>([]);
    const authKey = cacheService.GetAuthKey();

    // Cols def
    const cols: GridColDef[] = [
        {
            field: "date",
            headerName: t("common:date"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS),
        },
        {
            field: "description",
            headerName: t("common:description"),
            flex: CellSize.xlarge,
            minWidth: CellSize.xlarge,
            renderCell: (data: any) => data.value,
        },
        {
            field: "version",
            headerName: t("common:version"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => data.value,
        },
        {
            field: "fileName",
            headerName: t("table:headerName.fileName"),
            flex: CellSize.xlarge,
            minWidth: CellSize.xlarge,
            renderCell: (data: any) => (
                <Typography pt={0.5} pb={0.5}>
                    {data.value}
                </Typography>
            ),
        },
        {
            field: "actions",
            type: "actions",
            align: "right",
            flex: CellSize.actionsDefault,
            minWidth: CellSize.actionsDefault,
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem
                    icon={<CloudDownloadIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                    label={t("common:download")}
                    onClick={() => OpenLinkInNewTab(fileService.DownloadPlatformDocument(params?.row?.id, authKey!))}
                />,
            ],
        },
    ];

    React.useEffect(() => {
        ingestService.GetAsperaSettingsForRequestUpload().then((response) => {
            setSettings(response);
            if (response.length > 0) setSettingCurrent(response[0]);
        });

        if (UserHasAcl(AclTypes.aclDocumentsAccess))
            fileService.GetPlatformDocuments().then((response) => {
                if (response.documents)
                    setDocs(response.documents?.filter((x) => x.type === PlatformDocumentType.IngestSpecification));
            });
    }, []);

    const handleCancelClick = () => {
        closeModal();
    };

    return (
        <Modal
            title={t("modal:title.ingestHelpAndDocumentation")}
            reference={ref}
            closeModal={handleCancelClick}
            width={1000}
            footer={
                <Stack alignItems="flex-end">
                    <Button onClick={handleCancelClick}>{t("button:iGotIt")}</Button>
                </Stack>
            }
        >
            <Stack spacing={2}>
                <Typography variant="subtitle1">{t("modal:modalAsperaCredentials.subTitle")}</Typography>
                <Typography variant="body2">
                    {t("modal:modalAsperaCredentials.firstBody")}
                    <br />
                    <br />
                    {t("modal:modalAsperaCredentials.secondBody")}
                    <br />
                    <br />
                    {t("modal:modalAsperaCredentials.thirdBody")}
                </Typography>

                <Divider sx={{ mt: 2, mb: 2 }} />

                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle1">{t("modal:modalAsperaCredentials.asperaCredentials.subTitle")}</Typography>
                    <Autocomplete
                        options={settings}
                        value={settingsCurrent}
                        onChange={(e, v) => {
                            if (!v) return;
                            setSettingCurrent(v);
                        }}
                        getOptionLabel={(value: any) => value.provider?.labName}
                        isOptionEqualToValue={(option, value) => option.provider?.id === value.provider?.id}
                        filterOptions={(x, s) =>
                            x.filter((a) => a?.provider?.labName?.toString().toLowerCase().includes(s.inputValue.toLowerCase()))
                        }
                        renderInput={(params) => (
                            <OutlinedInput
                                {...params.InputProps}
                                inputProps={params.inputProps}
                                placeholder={t("field:placeholder.changeProvider")}
                                required
                                sx={{ width: 300 }}
                            />
                        )}
                    />
                </Stack>

                <Card sx={{ p: 2 }} variant="outlined">
                    <Grid container spacing={2}>
                        <Grid item md={4}>
                            <Typography variant="body2" fontWeight={500} color="text.tier">
                                {t("common:host")}
                            </Typography>
                            <Typography>{settingsCurrent?.provider?.asperaAccount?.asperaHost}</Typography>
                        </Grid>
                        <Grid item md={4}>
                            <Typography variant="body2" fontWeight={500} color="text.tier">
                                {t("common:sshPort")}
                            </Typography>
                            <Typography>{settingsCurrent?.provider?.asperaAccount?.asperaSshPort}</Typography>
                        </Grid>
                        <Grid item md={4}>
                            <Typography variant="body2" fontWeight={500} color="text.tier">
                                {t("common:faspPort")}
                            </Typography>
                            <Typography>{settingsCurrent?.provider?.asperaAccount?.asperaTcpPort}</Typography>
                        </Grid>
                        <Grid item md={4}>
                            <Typography variant="body2" fontWeight={500} color="text.tier">
                                {t("common:user")}
                            </Typography>
                            <Typography>{settingsCurrent?.provider?.asperaAccount?.asperaLogin}</Typography>
                        </Grid>
                        <Grid item md={4}>
                            <Typography variant="body2" fontWeight={500} color="text.tier">
                                {t("common:password")}
                            </Typography>
                            <Typography>{settingsCurrent?.provider?.asperaAccount?.asperaPassword}</Typography>
                        </Grid>
                    </Grid>
                </Card>

                {UserHasAcl(AclTypes.aclDocumentsAccess) && (
                    <>
                        <Divider sx={{ mt: 2, mb: 2 }} />

                        <Typography variant="subtitle1">{t("common:documents")}</Typography>

                        <DataTable
                            noControls
                            rows={docs}
                            columns={cols}
                            columnHeaderHeight={48}
                            rowHeight={56}
                            getRowHeight={() => "auto"}
                            disableRowSelectionOnClick
                            pagination={false}
                        />
                    </>
                )}
            </Stack>
        </Modal>
    );
});
export default ModalAsperaCredentials;
